'use client'

import { isEmpty, isEqual, omit } from 'lodash-es'
import { useEffect, useRef } from 'react'

import {
  defaultFilters,
  type TSearchFilter,
  useSearchFilterStore,
} from '@/entities/map'
import { useAirbridgeContext } from '@/shared/config'

import { buildSearchSemanticAttributes } from './search-event'

const AIRBRIDGE_EXCLUDED_FILTERS = [
  'minUsingFee',
  'maxUsingFee',
  'floors',
] as const

const defaultState = omit(defaultFilters, AIRBRIDGE_EXCLUDED_FILTERS)

const formatLabel = (startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) return ''

  const formatDate = (date: string) => date.replace(/-/g, '.')

  return `${formatDate(startDate)} ~ ${formatDate(endDate)}`
}

type LastSentEvent = {
  keyword: string
  filters: Omit<TSearchFilter, (typeof AIRBRIDGE_EXCLUDED_FILTERS)[number]>
}

export const AirbridgeEventSender = () => {
  const { isInitialized, sendEvent } = useAirbridgeContext()
  const filters = useSearchFilterStore(state => state.filters)
  const keyword = useSearchFilterStore(state => state.keyword)
  const lastSentEventRef = useRef<LastSentEvent>({
    keyword: '',
    filters: defaultState,
  })

  useEffect(() => {
    if (!isInitialized) {
      return
    }

    const airbridgeFilters = omit(filters, AIRBRIDGE_EXCLUDED_FILTERS)

    const lastSentEvent = lastSentEventRef.current
    const isKeywordChanged =
      lastSentEvent.keyword !== keyword && !isEmpty(keyword)
    const isFiltersChanged =
      !isEqual(lastSentEvent.filters, airbridgeFilters) &&
      !isEqual(defaultState, airbridgeFilters)

    if (!isKeywordChanged && !isFiltersChanged) {
      return
    }

    lastSentEventRef.current = { keyword, filters: airbridgeFilters }

    const { startDate, endDate } = airbridgeFilters

    sendEvent({
      event: 'airbridge.ecommerce.searchResults.viewed',
      params: {
        action: keyword,
        label: formatLabel(startDate, endDate),
        semanticAttributes: buildSearchSemanticAttributes(airbridgeFilters),
      },
    })
  }, [isInitialized, filters, keyword, sendEvent])

  return null
}
